// ** React Imports
import { useContext, useEffect, useMemo, useRef } from "react";
import { useHistory, matchPath } from "react-router-dom";
import { renderRoutes } from "react-router-config";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";
import { vendor } from "store/vendor";
import { injectReducer } from "store/reducer";
import { loadModalReducer } from "store/asyncReducers";

// ** Third Party Components
import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import ScrollPositionSaver from "components/other/ScrollPositionSaver";
import ButtonFloating from "components/layout/Button/ButtonFloating";
import CookieConsentBanner from "components/global/CookieConsentBanner/CookieConsentBanner";
import ScriptLoader from "components/global/ScriptLoader/ScriptLoader";

// ** Custom Hooks
import useModal from "hooks/useModal";

// ** Services
import { mainPageNavItemsFunc } from "../services/mainPageNavItems";

// ** Utils & Helpers
import pseudoFormat from "utils/pseudoFormat";
import containerRoutes from "helpers/containerRoutes";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";
import { MainProvider } from "../MainContext";

// ** Styled Components
import { StyledMainSection, StyledMainWrapper, StyledSpace } from "./styles";

// ** Lazy Loading Imports
const SideMenu = loadable(() => import("./SideMenu"));
const Header = loadable(() => import("components/global/Header/Header"));
const MainContent = loadable(() => import("./MainContent"));
const BottomFloatingBar = loadable(
	() => import("components/layout/BottomFloatingBar"),
	{
		ssr: false,
	}
);
const Modal = loadable(() => import("containers/Modal/Modal"), {
	ssr: false,
});
const ModalAuth = loadable(() => import("containers/ModalAuth/ModalAuth"), {
	ssr: false,
});
const ModalVerifyEmailRegistration = loadable(
	() => import("containers/ModalAuth/ModalVerifyEmailRegistration"),
	{
		ssr: false,
	}
);
const ModalVerifyPhoneRegistration = loadable(
	() => import("containers/ModalAuth/ModalVerifyPhoneRegistration"),
	{
		ssr: false,
	}
);
const Suspension = loadable(() => import("components/suspension"), {
	ssr: false,
});

// ** Configs
import routes from "config/routes";

// #####################################################

const MainWrapper = ({ locationPathname, aclid }) => {
	const { t } = useTranslation(["common", "pages"]);

	const history = useHistory();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const userFrozen = useSelector((state) => state.globalUser.frozen);
	const usersOnline = useSelector(
		(state) => state.globalCommon.statistics.usersOnline
	);
	const language = useSelector((state) => state.globalCommon.language);
	const isAuthModalOpen = useSelector(
		(state) => state.globalCommon.authModal.isOpen
	);

	const isRegistrationVerifyEmailModalOpen = useSelector(
		(state) => state.globalCommon.registrationVerifyEmailModal.isOpen
	);

	const isRegistrationVerifyPhoneModalOpen = useSelector(
		(state) => state.globalCommon.registrationVerifyPhoneModal.isOpen
	);

	const hasAnalyticsStorageConsent = useSelector(
		(state) => state.globalCommon.cookieConsent.settings.analytics_storage
	);

	const { isDesktop, isPhone } = useContext(deviceTypeContext);

	const { showModal } = useModal();

	const finalRoutes = userFrozen
		? containerRoutes(routes.filter(({ meta }) => meta?.suspended))
		: routes;

	const matchSelectedPaths =
		matchPath(locationPathname, {
			path: "/registration-code/:login?/:id?/:hash?",
			exact: true,
			sensitive: true,
			strict: false,
		}) ||
		matchPath(locationPathname, {
			path: "/registration-phone/:login?/:id?/:hash?",
			exact: true,
			sensitive: true,
			strict: false,
		}) ||
		matchPath(locationPathname, {
			path: "/registration-verify-email",
			exact: true,
			sensitive: true,
			strict: false,
		}) ||
		matchPath(locationPathname, {
			path: "/registration-form",
			exact: true,
			sensitive: true,
			strict: false,
		});

	const mainPageNavItems = useMemo(() => {
		return mainPageNavItemsFunc(t, usersOnline).map((item) => {
			if (item.key === "users") {
				item.content = () => (
					<Block style={{ lineHeight: "0.9rem" }}>
						<Block>{item.label}</Block>
						<Block>
							<Text small>
								Online{" "}
								<Text bold>{pseudoFormat(usersOnline)}</Text>
							</Text>
						</Block>
					</Block>
				);
			}

			return item;
		});
	}, [usersOnline, isAuth, language]);

	const mainWrapperRef = useRef(null);
	const headerRef = useRef(null);

	const isConversation = matchPath(locationPathname, {
		path: "/userpanel/messages/show/:id",
		exact: true,
	});

	const ctaTimestamp = useMemo(() => {
		return Date.now();
	}, []);

	useEffect(() => {
		if (
			!isConversation &&
			mainWrapperRef.current &&
			isPhone &&
			mainWrapperRef.current?.style?.position == "fixed" &&
			mainWrapperRef.current?.style?.height == "100%" &&
			mainWrapperRef.current?.style?.minHeight == "auto"
		) {
			mainWrapperRef.current.style.position = null;
			mainWrapperRef.current.style.height = null;
			mainWrapperRef.current.style.minHeight = null;
		}
	}, [isConversation]);

	const modalHash = useSelector((state) => state.modal?.modalHash);

	useEffect(() => {
		if (isAuth) {
			Modal.preload();

			const loadReducer = async () => {
				const reducer = await loadModalReducer();
				injectReducer(vendor.store, { key: "modal", reducer });
			};
			if (modalHash === undefined) {
				loadReducer();
			}
		}
	}, [isAuth]);

	// Event for a feature in the text editor that prevents focus when clicking outside the editor but close to it.
	useEffect(() => {
		if (typeof window === "undefined" || !window) return;

		const trackMouseDown = (e) => {
			window.lastClickTarget = e.target;
		};

		document.addEventListener("mousedown", trackMouseDown);

		return () => {
			document.removeEventListener("mousedown", trackMouseDown);
		};
	}, []);

	// #####################################################

	return (
		<>
			<ScrollPositionSaver
				/* WARNING: Don't remove the "mainWrapperRef" prop, because component stop working.
				 Be careful when moving the component, it could make it stop working. */
				mainWrapperRef={mainWrapperRef}
			/>
			<Header
				ref={headerRef}
				id="header"
				locationPathname={locationPathname}
				mainPageNavItems={mainPageNavItems}
			/>
			{isAuth && showModal && (
				<Modal headerRef={headerRef} mainWrapperRef={mainWrapperRef} />
			)}
			<StyledMainWrapper ref={mainWrapperRef}>
				<StyledMainSection id="main" as="section" showTopMenu={true}>
					{isDesktop ? (
						<SideMenu mainPageNavItems={mainPageNavItems} />
					) : null}
					<MainContent locationPathname={locationPathname}>
						<Suspension />
						<MainProvider value={{ mainWrapperRef }}>
							{renderRoutes(finalRoutes)}
						</MainProvider>

						{isAuth && <BottomFloatingBar />}

						{/* CPA TESTING */}
						{isAuth && hasAnalyticsStorageConsent && (
							<>
								<img
									id="20655_cpa_testing"
									src={`https://tsyndicate.com/api/v2/cpa/20655/pixel.gif?goalid=2479`}
									width="1"
									height="1"
									border="0"
									alt="20655_cpa_testing"
									style={{
										visibility: "hidden",
										display: "block",
									}}
								/>
								<img
									id="1000524292_cpa_testing"
									src={`https://ads.trafficjunky.net/ct?a=1000524292&member_id=1000428751&cb=${ctaTimestamp}&ctv=0.05&aclid=${aclid}`}
									width="1"
									height="1"
									border="0"
									alt="cpa_testing"
									style={{
										visibility: "hidden",
										display: "block",
									}}
								/>
								<ScriptLoader
									src="https://a.magsrv.com/tag_gen.js"
									async
									defer
									attributes={{
										"data-goal":
											"399583625a96e0961465219c0bffa046",
									}}
								/>
							</>
						)}
					</MainContent>
				</StyledMainSection>
				{!isAuth && !matchSelectedPaths && (
					<>
						<StyledSpace
							spaceHeight={"68px"}
							isDesktop={isDesktop}
						/>
						<ButtonFloating
							onClick={() => {
								history.push({
									pathname: "/",
									state: "register",
								});
								window.scrollTo(0, 0);
							}}
						>
							{t("landing:registerForFree")}
						</ButtonFloating>
					</>
				)}
			</StyledMainWrapper>

			{/* Baanner zgody cookie */}
			<CookieConsentBanner />
			{isAuthModalOpen && <ModalAuth />}
			{isRegistrationVerifyEmailModalOpen && (
				<ModalVerifyEmailRegistration />
			)}
			{isRegistrationVerifyPhoneModalOpen && (
				<ModalVerifyPhoneRegistration />
			)}
		</>
	);
};

// #####################################################

export default MainWrapper;

// #####################################################
